import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import {
  AlertCircle,
  ArrowLeft,
  Bot,
  ChevronDown,
  CircleCheck,
  ExternalLink,
  MoreHorizontal,
  Paperclip,
  RotateCcw,
  Send,
  UserRound,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { LoadingButton } from '@/components/loading-button'
import { ReplyAttachments } from '@/components/reply-attachments'
import type { ReplyAttachmentDraft } from '@/features/communications/types'
import { StaffAssignmentPicker } from '@/components/staff-assignment-picker'
import { useCurrentUser } from '@/api/hooks'
import { PHONE_QUERY, useMediaQuery } from '@/hooks/use-media-query'
import { formatApiErrorMessage, getApiErrorMessage } from '@/lib/api-error'
import { shopifyAdminCustomerUrl } from '@/lib/shopify-product-links'
import { cn, formatEventTime } from '@/lib/utils'
import { SenderAvatar } from '@/features/communications/message-row'
import { ChatMessageRow } from '@/features/storefront-chat/chat-message'
import { ChatReplyRow } from '@/features/storefront-chat/chat-reply'
import { CurrentPage, PageIcon, PageTrail, StoreLink } from '@/features/storefront-chat/pages'
import { Fold, StaffContextSections } from '@/features/storefront-chat/staff-context'
import { hasStaffContext, isOnPageNow, pageLabel, shopperDisplayName } from '@/features/storefront-chat/format'
import {
  useAssignChat,
  useCloseChat,
  useReopenChat,
  useReplyToChat,
  useSetChatMode,
  useStorefrontChat,
  useStorefrontChatContext,
  useStreamingAnswer,
} from '@/features/storefront-chat/hooks'
import type { ChatContext, ChatDetail } from '@/features/storefront-chat/types'
import { ComposeTextarea } from '@/components/compose-field'

/** Take the chat over from the AI, or hand it back: under the reply box, where a person decides. */
function ModeControl({ chat, me }: { chat: ChatDetail; me: { id: number; name: string } | undefined }) {
  const mode = useSetChatMode(chat.conversation_id, me)
  const human = chat.mode === 'human'
  const set = (next: 'ai' | 'human') =>
    mode.mutate(next, {
      onError: (err) =>
        toast.error(formatApiErrorMessage(next === 'human' ? 'Take over failed' : 'Hand back failed', err)),
    })
  return human ? (
    <LoadingButton variant="outline" loading={mode.isPending} loadingText="Handing back..." onClick={() => set('ai')}>
      <Bot className="h-4 w-4" /> Hand back to AI
    </LoadingButton>
  ) : (
    <LoadingButton variant="outline" loading={mode.isPending} loadingText="Taking over..." onClick={() => set('human')}>
      <UserRound className="h-4 w-4" /> Take over from AI
    </LoadingButton>
  )
}

/** Close a chat that is over or needs no reply (USA-1478); a closed chat can be reopened. */
function CloseControl({ chat, me }: { chat: ChatDetail; me: { id: number; name: string } | undefined }) {
  const close = useCloseChat(chat.conversation_id, me)
  const reopen = useReopenChat(chat.conversation_id)
  if (chat.closed_at) {
    return (
      <LoadingButton
        variant="ghost"
        loading={reopen.isPending}
        loadingText="Reopening..."
        disabled={close.isPending}
        onClick={() =>
          reopen.mutate(undefined, { onError: (err) => toast.error(formatApiErrorMessage('Reopen failed', err)) })
        }
      >
        <RotateCcw className="h-4 w-4" /> Reopen
      </LoadingButton>
    )
  }
  return (
    <LoadingButton
      variant="ghost"
      loading={close.isPending}
      loadingText="Closing..."
      disabled={reopen.isPending}
      onClick={() =>
        close.mutate(undefined, { onError: (err) => toast.error(formatApiErrorMessage('Close failed', err)) })
      }
    >
      <CircleCheck className="h-4 w-4" /> Close chat
    </LoadingButton>
  )
}

/** On a phone the reply bar keeps one row: attaching, taking over or handing back, and closing
 * or reopening sit behind "More". */
function ChatActionsMenu({
  chat,
  me,
  onAttach,
  attachDisabled,
}: {
  chat: ChatDetail
  me: { id: number; name: string } | undefined
  onAttach: () => void
  attachDisabled: boolean
}) {
  const mode = useSetChatMode(chat.conversation_id, me)
  const close = useCloseChat(chat.conversation_id, me)
  const reopen = useReopenChat(chat.conversation_id)
  const human = chat.mode === 'human'
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="size-10 shrink-0" aria-label="More">
          <MoreHorizontal className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="min-w-52">
        <DropdownMenuItem disabled={attachDisabled} onSelect={onAttach}>
          <Paperclip /> Attach files
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={mode.isPending}
          onSelect={() =>
            mode.mutate(human ? 'ai' : 'human', {
              onError: (err) =>
                toast.error(formatApiErrorMessage(human ? 'Hand back failed' : 'Take over failed', err)),
            })
          }
        >
          {human ? (
            <>
              <Bot /> Hand back to AI
            </>
          ) : (
            <>
              <UserRound /> Take over from AI
            </>
          )}
        </DropdownMenuItem>
        {chat.closed_at ? (
          <DropdownMenuItem
            disabled={reopen.isPending}
            onSelect={() =>
              reopen.mutate(undefined, { onError: (err) => toast.error(formatApiErrorMessage('Reopen failed', err)) })
            }
          >
            <RotateCcw /> Reopen
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            disabled={close.isPending}
            onSelect={() =>
              close.mutate(undefined, { onError: (err) => toast.error(formatApiErrorMessage('Close failed', err)) })
            }
          >
            <CircleCheck /> Close chat
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// What the chat takes (app/services/storefront_chat/attachments.py).
const CHAT_FILE_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.xlsx,.xls,.csv,.docx,.doc,.txt'
const CHAT_MAX_FILES = 5
const CHAT_MAX_FILE_BYTES = 10 * 1024 * 1024

function Composer({
  chat,
  me,
  phone,
}: {
  chat: ChatDetail
  me: { id: number; name: string } | undefined
  phone: boolean
}) {
  const reply = useReplyToChat(chat.conversation_id)
  const picker = useRef<HTMLInputElement | null>(null)
  const [text, setText] = useState('')
  const [files, setFiles] = useState<ReplyAttachmentDraft[]>([])
  const [attaching, setAttaching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const takesOver = chat.mode === 'ai'
  const canSend = (text.trim().length > 0 || files.length > 0) && !attaching
  const send = () => {
    const body = text.trim()
    if (!canSend || reply.isPending) return
    setError(null)
    reply.mutate(
      { body, files },
      {
        onSuccess: () => {
          setText('')
          setFiles([])
        },
        onError: (err) => setError(getApiErrorMessage(err, 'Check your connection and try again.')),
      },
    )
  }
  const errorBanner = error && (
    <div role="alert" className="mb-2 flex gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-2.5 text-sm">
      <AlertCircle aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
      <div>
        <p className="font-medium text-foreground">Reply not sent</p>
        <p className="mt-0.5 text-foreground">{error}</p>
      </div>
    </div>
  )
  const attachmentsField = (
    <ReplyAttachments
      attachments={files}
      onChange={setFiles}
      attaching={attaching}
      onAttachingChange={setAttaching}
      disabled={reply.isPending}
      maxFiles={CHAT_MAX_FILES}
      maxFileBytes={CHAT_MAX_FILE_BYTES}
      maxTotalBytes={CHAT_MAX_FILES * CHAT_MAX_FILE_BYTES}
      accept={CHAT_FILE_ACCEPT}
      compact={phone}
      pickerRef={picker}
    />
  )
  const replyBox = (
    <ComposeTextarea
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault()
          send()
        }
      }}
      rows={phone ? 1 : 2}
      aria-label={`Reply to ${shopperDisplayName(chat)}`}
      placeholder={`Reply to ${shopperDisplayName(chat)}`}
      // 16px on touch screens: iPhones zoom into any smaller text box when it is tapped.
      className={cn(
        'max-h-48 flex-1 resize-none bg-background text-base md:text-base pointer-fine:text-sm md:pointer-fine:text-sm',
        phone ? 'max-h-32 min-h-10 py-2' : 'min-h-[2.75rem]',
      )}
      disabled={reply.isPending}
    />
  )

  if (phone) {
    return (
      // While typing, the keyboard covers the home indicator: its safe-area gap goes too.
      <div className="border-t bg-muted/20 px-2 pt-1.5 pb-[max(0.375rem,env(safe-area-inset-bottom))] has-[textarea:focus]:pb-1.5">
        {errorBanner}
        <div className={cn(files.length > 0 && 'mb-2')}>{attachmentsField}</div>
        <div className="flex items-end gap-1.5">
          <ChatActionsMenu
            chat={chat}
            me={me}
            onAttach={() => picker.current?.click()}
            attachDisabled={reply.isPending || attaching}
          />
          {replyBox}
          <LoadingButton
            loading={reply.isPending}
            onClick={send}
            disabled={!canSend}
            size="icon"
            className="size-10 shrink-0"
            aria-label={takesOver ? 'Take over and send' : 'Send'}
          >
            <Send className="h-4 w-4" />
          </LoadingButton>
        </div>
      </div>
    )
  }

  return (
    <div className="border-t bg-muted/20 px-3 py-3 sm:px-4">
      {errorBanner}
      <div className="flex items-end gap-2">
        {replyBox}
        <LoadingButton
          loading={reply.isPending}
          loadingText="Sending..."
          onClick={send}
          disabled={!canSend}
          className="shrink-0"
        >
          <Send className="h-3.5 w-3.5" />
          {takesOver ? 'Take over & send' : 'Send'}
        </LoadingButton>
      </div>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-2">
        {attachmentsField}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <CloseControl chat={chat} me={me} />
          <ModeControl chat={chat} me={me} />
        </div>
      </div>
    </div>
  )
}

/** The shopper's current page, then one closed line each for what a person needs to take over
 * (our pricing on the product they are viewing, their cart, the customer) and their page trail;
 * a rail beside the messages when there is room. */
function PageContext({
  chat,
  context,
  variant,
}: {
  chat: ChatDetail
  context: ChatContext | undefined
  variant: 'rail' | 'bar'
}) {
  const [details, setDetails] = useState(false)
  const trail = chat.pages.length > 1
  const showDetails = hasStaffContext(context) || trail
  if (!chat.last_page && !showDetails) return null
  const sections = (
    <StaffContextSections context={context}>
      {trail && (
        <Fold label="Pages visited" summary={chat.pages.length}>
          <PageTrail pages={chat.pages} />
        </Fold>
      )}
    </StaffContextSections>
  )
  if (variant === 'rail') {
    return (
      <aside aria-label="Shopper's pages" className="flex min-h-0 w-full flex-col gap-3 overflow-y-auto border-l bg-muted/30 p-3">
        {chat.last_page && <CurrentPage page={chat.last_page} seenAt={chat.last_page_seen_at} />}
        {sections}
      </aside>
    )
  }
  return (
    <div className="border-b bg-muted/30 px-3 py-2 sm:px-4">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          {chat.last_page && <CurrentPage page={chat.last_page} seenAt={chat.last_page_seen_at} compact />}
        </div>
        {showDetails && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 shrink-0 px-2 text-xs"
            aria-expanded={details}
            onClick={() => setDetails(!details)}
          >
            Details
            <ChevronDown aria-hidden className={cn('h-3.5 w-3.5 transition-transform', details && 'rotate-180')} />
          </Button>
        )}
      </div>
      {details && <div className="mt-2 max-h-[60vh] overflow-y-auto border-t pt-2">{sections}</div>}
    </div>
  )
}

/** Why the AI handed off. On a narrow pane the reason sits under the label at full width, two
 * lines until tapped, so the messages keep the screen. */
function NeedsPerson({ reason, at }: { reason?: string | null; at?: string | null }) {
  const [open, setOpen] = useState(false)
  return (
    <div
      role="status"
      className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-x-2 border-t px-3 py-2 text-sm sm:px-4"
    >
      <span aria-hidden className="h-2 w-2 translate-y-[-1px] rounded-full bg-destructive" />
      <p className="min-w-0 text-muted-foreground">
        <span className="font-medium text-foreground">Needs a person</span>
        {reason && <span className="@max-[36rem]/chat:hidden"> · {reason}</span>}
      </p>
      {at ? (
        <time dateTime={at} className="text-xs tabular-nums text-muted-foreground">
          {formatEventTime(at)}
        </time>
      ) : (
        <span />
      )}
      {reason && (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className={cn(
            'col-span-2 col-start-2 mt-0.5 rounded-sm text-left text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 @[36rem]/chat:hidden',
            !open && 'line-clamp-2',
          )}
        >
          {reason}
        </button>
      )}
    </div>
  )
}

/** The shopper's email, linked to their store account when they are signed in. */
function ShopperContact({ chat }: { chat: ChatDetail }) {
  const name = shopperDisplayName(chat)
  const customerUrl = shopifyAdminCustomerUrl(chat.customer?.shopify_customer_id)
  const email = chat.shopper_email || chat.customer?.email
  if (!(email && email !== name) && !customerUrl) return null
  return (
    <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
      {email && email !== name && <span className="truncate">{email}</span>}
      {email && email !== name && customerUrl && <span aria-hidden>·</span>}
      {customerUrl && (
        <StoreLink
          href={customerUrl}
          className="inline-flex items-center gap-1 font-medium text-usa-blue underline-offset-2 hover:underline"
        >
          Signed-in customer <ExternalLink aria-hidden className="h-3 w-3" />
        </StoreLink>
      )}
    </p>
  )
}

/** On a phone the shopper's name heads the screen with one line under it ("Needs a person", their
 * page); tapping it slides up everything else (USA-1746), so the messages keep the screen. */
function PhoneChatDetails({ chat, context }: { chat: ChatDetail; context: ChatContext | undefined }) {
  const [open, setOpen] = useState(false)
  const sheet = useRef<HTMLDivElement>(null)
  const name = shopperDisplayName(chat)
  const trail = chat.pages.length > 1
  const reason = chat.needs_person ? chat.handoff_reason : null
  const live = isOnPageNow(chat.last_page_seen_at)
  const email = chat.shopper_email || chat.customer?.email
  return (
    // The name opens the sheet as its trigger, so closing it puts focus back on the name.
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="flex min-h-11 min-w-0 flex-1 items-center gap-1 rounded-md px-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-base font-semibold leading-tight text-foreground">
              {name}
              <span className="sr-only">, details</span>
            </span>
            <span className="flex min-w-0 items-center gap-1.5 text-xs leading-tight text-muted-foreground">
              {chat.needs_person && (
                <span className="inline-flex shrink-0 items-center gap-1.5 font-medium text-foreground">
                  <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-destructive" />
                  Needs a person
                </span>
              )}
              {chat.last_page ? (
                <>
                  {chat.needs_person && <span aria-hidden>·</span>}
                  {live && (
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500">
                      <span className="sr-only">On this page now:</span>
                    </span>
                  )}
                  <PageIcon page={chat.last_page} className="h-3 w-3" />
                  <span className="truncate">{pageLabel(chat.last_page)}</span>
                </>
              ) : (
                !chat.needs_person && email && email !== name && <span className="truncate">{email}</span>
              )}
            </span>
          </span>
          <ChevronDown aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </SheetTrigger>
      <SheetContent
        ref={sheet}
        side="bottom"
        aria-describedby={undefined}
        // Focus the sheet, not its first link.
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          sheet.current?.focus()
        }}
        className="max-h-[85dvh] overflow-y-auto rounded-t-xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 outline-none"
      >
        <div className="space-y-3">
          <div className="pr-8">
            <SheetTitle className="truncate text-base font-semibold">{name}</SheetTitle>
            <ShopperContact chat={chat} />
          </div>
          {chat.needs_person && (
            <p className="flex gap-2 text-sm text-muted-foreground">
              <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-destructive" />
              <span>
                <span className="font-medium text-foreground">Needs a person</span>
                {reason && <> · {reason}</>}
                {chat.handoff_requested_at && (
                  <time dateTime={chat.handoff_requested_at} className="ml-1.5 text-xs tabular-nums">
                    · {formatEventTime(chat.handoff_requested_at)}
                  </time>
                )}
              </span>
            </p>
          )}
          {chat.last_page && <CurrentPage page={chat.last_page} seenAt={chat.last_page_seen_at} />}
          <StaffContextSections context={context}>
            {trail && (
              <Fold label="Pages visited" summary={chat.pages.length}>
                <PageTrail pages={chat.pages} />
              </Fold>
            )}
          </StaffContextSections>
        </div>
      </SheetContent>
    </Sheet>
  )
}

function ChatHeader({
  chat,
  context,
  phone,
}: {
  chat: ChatDetail
  context: ChatContext | undefined
  phone: boolean
}) {
  const assign = useAssignChat(chat.conversation_id)
  const name = shopperDisplayName(chat)
  return (
    // On a phone the chat is the whole screen: the header sits below the iPhone status bar.
    <header className={cn('border-b', phone && 'pt-[env(safe-area-inset-top)]')}>
      <div
        className={cn(
          'flex flex-wrap items-center gap-y-2 px-3 sm:px-4',
          phone ? 'flex-nowrap gap-x-1 py-0.5 pl-1 pr-2' : 'gap-x-3 py-3',
        )}
      >
        <Link
          to="/customer-comms/chats"
          search={true}
          className={cn(
            'flex shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 lg:hidden',
            phone ? 'size-10 text-foreground' : '-ml-1 p-1',
          )}
          aria-label="All chats"
        >
          <ArrowLeft className={phone ? 'h-5 w-5' : 'h-4 w-4'} />
        </Link>
        {phone ? (
          <PhoneChatDetails chat={chat} context={context} />
        ) : (
          <>
            <SenderAvatar tone="counterpart" name={name} />
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-base font-semibold text-foreground">{name}</h2>
              <ShopperContact chat={chat} />
            </div>
          </>
        )}
        <div className="flex min-w-0 shrink-0 items-center justify-end gap-2">
          <StaffAssignmentPicker
            label="Owner"
            showName
            // On a narrow pane the owner's name says enough; the "Owner" label gives way to it. On a
            // phone their photo alone does (the name is on its label), leaving the shopper's name room.
            className={cn(
              'min-w-0 max-w-full',
              chat.assigned_staff_id && '@max-[36rem]/chat:[&>span:nth-child(2)]:hidden',
              phone && chat.assigned_staff_id && 'pr-1.5 [&>span:nth-child(3)]:hidden',
            )}
            currentUserId={chat.assigned_staff_id}
            pending={assign.isPending}
            onChange={(user) =>
              assign.mutate(user ? { id: user.id, name: user.name } : null, {
                onError: (err) => toast.error(formatApiErrorMessage('Assign failed', err)),
              })
            }
          />
        </div>
      </div>
      {chat.needs_person && !phone && <NeedsPerson reason={chat.handoff_reason} at={chat.handoff_requested_at} />}
    </header>
  )
}

function ChatMessages({ chat, me }: { chat: ChatDetail; me: { id: number; name: string } | undefined }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const pinned = useRef(true)
  const stream = useStreamingAnswer(chat.conversation_id, chat.messages)
  const name = shopperDisplayName(chat)
  const lastId = chat.messages[chat.messages.length - 1]?.id

  useEffect(() => {
    pinned.current = true
  }, [chat.conversation_id])

  // Keep the newest message in view unless staff scrolled up to read.
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el && pinned.current) el.scrollTop = el.scrollHeight
  }, [chat.conversation_id, lastId, stream?.text, chat.closed_at])

  // When the list gets shorter (the phone keyboard opens, the reply box grows), keep the newest
  // message in view the same way.
  useEffect(() => {
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      if (pinned.current) el.scrollTop = el.scrollHeight
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={scrollRef}
      onScroll={(e) => {
        const el = e.currentTarget
        pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
      }}
      className="min-h-0 flex-1 overflow-y-auto"
    >
      <ol aria-label="Messages" aria-live="polite" className="space-y-1 py-3 @max-[36rem]/chat:space-y-0.5 @max-[36rem]/chat:py-2">
        {chat.messages.map((message) => (
          <li key={message.id}>
            {message.author === 'shopper' ? (
              <ChatMessageRow message={message} shopperName={name} />
            ) : (
              <ChatReplyRow
                conversationId={chat.conversation_id}
                message={message}
                shopperName={name}
                readAt={message.id === chat.read_through_message_id ? chat.read_at : null}
                me={me}
              />
            )}
          </li>
        ))}
        {chat.closed_at && !stream && (
          <li className="flex justify-center px-4 pt-2">
            <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <CircleCheck aria-hidden className="h-3.5 w-3.5" />
              Closed{chat.closed_by_name && ` by ${chat.closed_by_name}`} ·
              <time dateTime={chat.closed_at} className="tabular-nums">
                {formatEventTime(chat.closed_at)}
              </time>
            </p>
          </li>
        )}
        {stream && (
          <li>
            <ChatMessageRow
              message={{ author: 'ai', body: stream.text, created_at: new Date().toISOString() }}
              shopperName={name}
              streaming
            />
          </li>
        )}
      </ol>
    </div>
  )
}

export function ChatConversation({ conversationId }: { conversationId: string }) {
  const { data: chat, isLoading, error } = useStorefrontChat(conversationId)
  const { data: user } = useCurrentUser()
  const me = user ? { id: user.id, name: user.name } : undefined
  const { data: context } = useStorefrontChatContext(conversationId)
  const phone = useMediaQuery(PHONE_QUERY)

  if (isLoading) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-12 w-2/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }
  if (!chat) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <p className="text-sm text-muted-foreground">
          {error ? 'This chat could not be loaded.' : 'This chat no longer exists.'}
        </p>
        <Button asChild size="sm" variant="outline">
          <Link to="/customer-comms/chats">All chats</Link>
        </Button>
      </div>
    )
  }
  return (
    <div className="@container/chat flex h-full min-h-0 min-w-0 flex-col">
      <ChatHeader chat={chat} context={context} phone={phone} />
      <div className="grid min-h-0 min-w-0 flex-1 grid-cols-[minmax(0,1fr)] @[52rem]/chat:grid-cols-[minmax(0,1fr)_17rem]">
        <div className="flex min-h-0 min-w-0 flex-col">
          {!phone && (
            <div className="@[52rem]/chat:hidden">
              <PageContext chat={chat} context={context} variant="bar" />
            </div>
          )}
          <ChatMessages chat={chat} me={me} />
          <Composer key={chat.conversation_id} chat={chat} me={me} phone={phone} />
        </div>
        <div className="hidden min-h-0 @[52rem]/chat:flex">
          <PageContext chat={chat} context={context} variant="rail" />
        </div>
      </div>
    </div>
  )
}
